import {useEffect, VFC} from 'react';
import gql from 'graphql-tag';
import {useQuery} from 'services/apollo';
import {useParams, useHistory} from 'react-router-dom';
import LoadingSpinner from 'components/LoadingSpinner';
import NotFound from 'components/NotFound';
import {EzAlert, EzBlankState, EzLayout, EzLink} from '@ezcater/recipe';
import PhoneNumbers from 'constants/PhoneNumbers';
import {FlashMessages} from '@ezcater/flash-messages';
import {TRACK_DRIVER_ASSIGNMENT_PAGE_NAME} from 'util/tracking/trackingEvents';
import {useDriverPageViewTracking} from 'util/tracking';
import {useTranslation} from 'react-i18next';
import {DELIVERIES_PATH} from 'paths';
import {useCurrentAccount} from 'components/CurrentAccount';
import {
  DeliveryStatus,
  type DriverProgressAssignmentQuery,
  type DriverProgressAssignmentQueryVariables,
} from 'services/graphql/types';
import FeedbackForm from 'components/FeedbackForm';
import {StickyFooter} from 'components/FeedbackForm/FeedbackForm.styles';
import Layout from '../Layout';
import {
  ContentsContainer,
  ScrollBarContainer,
  ScrollBarContent,
  SummaryBarTitleContainer,
  DeliveryCompletedAlertWrapper,
} from './DriverProgress.styles';
import ActionButton from './ActionButton';
import StartDelivery from './StartDelivery';
import InProgress from './InProgress';

export const AssignmentQuery = gql(/* GraphQL */ `
  query DriverProgressAssignmentQuery($id: ID!) {
    me {
      driverAccount {
        assignment(id: $id) {
          deliveryId
          deliverySetup
          distanceInMiles
          driverAskFor
          dropoffAddress {
            city
            id
            instructions
            locationName
            phone
            state
            street1
            street2
            zip
          }
          dropoffWindow {
            endAt
            startAt
          }
          durationInMinutes
          eventAt
          feedback {
            message
          }
          formattedOrderNumber
          id
          orderItems {
            name
            quantity
          }
          pickupAddress {
            city
            id
            instructions
            locationName
            state
            street1
            street2
            zip
          }
          status
        }
        id
      }
    }
  }
`);

const NotFoundPage: VFC = () => {
  const {t} = useTranslation();
  return (
    <NotFound title={t('driverProgress.notFound.title')}>
      {t('driverProgress.notFound.message')}
      <EzLink href={`tel:${PhoneNumbers.EZDISPATCH_DIRECT}`}>
        {PhoneNumbers.EZDISPATCH_DIRECT}
      </EzLink>
      .
    </NotFound>
  );
};

const DriverProgress = () => {
  const {t} = useTranslation();
  const history = useHistory();
  const {driverAssignmentId} = useParams<{driverAssignmentId: string}>();
  const currentAccount = useCurrentAccount();
  const currentAccountId = currentAccount.id || undefined;
  const {data, loading, error} = useQuery<
    DriverProgressAssignmentQuery,
    DriverProgressAssignmentQueryVariables
  >(AssignmentQuery, {variables: {id: driverAssignmentId}});
  const assignment = data?.me?.driverAccount?.assignment;

  useDriverPageViewTracking(
    TRACK_DRIVER_ASSIGNMENT_PAGE_NAME,
    assignment?.deliveryId,
    currentAccountId
  );

  useEffect(() => {
    if (assignment?.status === DeliveryStatus.Expired) {
      history.replace(DELIVERIES_PATH);
    }
  }, [assignment?.status, history]);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error || !assignment) {
    return <NotFoundPage />;
  }

  return (
    <Layout
      title={`${assignment.formattedOrderNumber} - ezCater Delivery`}
      orderNumber={assignment.formattedOrderNumber}
    >
      <ContentsContainer>
        <FlashMessages />
        <ScrollBarContainer>
          <SummaryBarTitleContainer>
            {t('driverProgress.header.orderNumber', {orderNumber: assignment.formattedOrderNumber})}
          </SummaryBarTitleContainer>
          <ScrollBarContent>
            {assignment.status === DeliveryStatus.Assigned && (
              <StartDelivery assignment={assignment} />
            )}
            {assignment.status === DeliveryStatus.InProgress && (
              <InProgress assignment={assignment} />
            )}
            {assignment.status === DeliveryStatus.Completed && (
              <EzLayout layout="stack">
                <DeliveryCompletedAlertWrapper>
                  <EzAlert use="success" headline="Delivery Completed" />
                </DeliveryCompletedAlertWrapper>
                <FeedbackForm driverAssignmentId={driverAssignmentId} />
              </EzLayout>
            )}
            {!!assignment.feedback && (
              <EzLayout layout="stack">
                <EzBlankState title="Feedback received" message="Thank you for your feedback" />
              </EzLayout>
            )}
          </ScrollBarContent>
          <StickyFooter>
            <ActionButton
              eventAt={assignment.eventAt}
              driverAssignmentId={driverAssignmentId}
              status={assignment.status}
            />
          </StickyFooter>
        </ScrollBarContainer>
      </ContentsContainer>
    </Layout>
  );
};

export default DriverProgress;
